import { getHighestNumber } from '@/util/number';
import VariableChip from '@/components/common/chips/VariableChip';
import VariablePicker from '@/components/nodes/settings/variable/VariablePicker';
import { useMetadata } from '@/hooks/useMetadata';
import { Application } from '@/types/application';
import { VariableRef } from '@/types/variable';
import { isString, isVariable } from '@/util/typeGuards';
import { getScopeType, getVariableTypeLabel } from '@/util/variables';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { FC, useState } from 'react';

type NewPromptGroupDialogProps = {
  open: boolean;
  application: Application;
  selectedSubroutineId: number | undefined;
  onClose: () => void;
  setApplication: (newApplication: Application) => void;
};

const NewPromptGroupDialog: FC<NewPromptGroupDialogProps> = (props) => {
  const { metadata } = useMetadata();
  const [name, setName] = useState<string>();
  const [path, setPath] = useState<VariableRef | string>();
  const [openVars, setOpenVars] = useState(false);

  const handleClick = () => {
    setOpenVars(true);
  };

  const handleConfirm = () => {
    if (!name) return;
    addNewPromptGroup(name, path);
    setName('');
    setPath('');
    props.onClose();
  };

  const addNewPromptGroup = (newName: string, newPath: VariableRef | string | undefined) => {
    const newPromptGroup = {
      id: getHighestNumber(props.application.promptGroups?.map((promptGroup) => promptGroup.id)) + 1,
      name: newName,
      path: newPath
    };

    props.setApplication({
      ...props.application,
      promptGroups: props.application.promptGroups
        ? [...props.application.promptGroups, newPromptGroup]
        : [newPromptGroup]
    });
  };

  const variableType = isVariable(path)
    ? getVariableTypeLabel(path, props.application, metadata, props.selectedSubroutineId)
    : undefined;

  return (
    <Dialog open={props.open} onClose={props.onClose} disableRestoreFocus>
      <DialogTitle>{'Create Prompt Group'}</DialogTitle>
      <DialogContent>
        <TextField
          label={'name'}
          size='small'
          fullWidth
          autoFocus={props.open}
          value={name}
          onChange={(event) => setName(event.target.value)}
          sx={{ mt: 1 }}
          InputProps={{
            autoComplete: 'off'
          }}
        />
        <Box display={'flex'} mt={2} gap={2}>
          <TextField
            label={'path'}
            size='small'
            value={isString(path) ? path : ''}
            onChange={(event) => setPath(event.target.value)}
            sx={{ mt: 1 }}
            InputProps={{
              autoComplete: 'off'
            }}
          />
          <VariableChip
            onClick={handleClick}
            onDelete={() => setPath('')}
            type={undefined}
            variableType={variableType}
            value={!isString(path) ? path : undefined}
            chipText={!isString(path) ? path?.name : undefined}
            scope={!isString(path) ? path?.scope : undefined}
          />
          <VariablePicker
            subroutineId={1}
            open={openVars}
            onClose={() => setOpenVars(false)}
            type={['string']}
            scope={getScopeType('promptgroups', metadata)}
            onVariablePicked={(variable) => setPath(variable)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} color='primary'>
          {'Cancel'}
        </Button>
        <Button onClick={handleConfirm} disabled={!name} color='primary'>
          {'Ok'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewPromptGroupDialog;
